import { useEffect, useMemo, useState } from 'react'
import { Stack } from '@mui/material'
import {
  CONTENT_IDEA_AUTO_ORIGINS,
  CONTENT_IDEA_ORIGINS,
  CONTENT_IDEA_PRIORITIES,
  CONTENT_IDEA_TYPES,
  CONTENT_INDUSTRIES,
  CONTENT_SOLUTIONS,
  type ContentIdea,
  type ContentIdeaOrigin,
  type ContentIdeaPriority,
  type ContentIdeaType,
  type ContentIndustry,
  type CreateContentIdeaRequest,
  type MetricSolution,
} from '@repo/types'
import { Button, Input, Modal } from '@repo/ui'

import { SelectField } from 'components'

import { industryLabel, originLabel, solutionLabel, typeLabel } from './contentIdeaModel'

/**
 * VRG-740 — add or edit a content idea. Terms are one per line, the first is the primary; the API
 * links any that match the keyword master (keyword or variant) and fills their cluster and intent.
 * Status moves are not here: they are the drawer's actions, each recorded with who and when.
 */
type Form = {
  title: string
  type: ContentIdeaType
  origin: ContentIdeaOrigin
  priority: string
  solutions: MetricSolution[]
  industries: ContentIndustry[]
  terms: string
  rationale: string
  notes: string
  draft_url: string
}

const formOf = (idea: ContentIdea | null | undefined): Form => ({
  title: idea?.title ?? '',
  type: idea?.type ?? 'explainer',
  origin: idea?.origin ?? 'manual',
  priority: idea?.priority ?? '',
  solutions: idea?.solutions ?? [],
  industries: idea?.industries ?? [],
  terms: (idea?.terms ?? []).map((t) => t.term).join('\n'),
  rationale: idea?.rationale ?? '',
  notes: idea?.notes ?? '',
  draft_url: idea?.draft_url ?? '',
})

const blank = (s: string) => (s.trim() ? s.trim() : null)

const ORIGIN_OPTIONS = CONTENT_IDEA_ORIGINS.filter((o) => !CONTENT_IDEA_AUTO_ORIGINS.includes(o)).map((o) => ({
  value: o,
  label: originLabel(o),
}))

export const ContentIdeaModal = ({
  open,
  idea,
  loading,
  onClose,
  onSave,
}: {
  open: boolean
  idea?: ContentIdea | null
  loading: boolean
  onClose: () => void
  onSave: (input: CreateContentIdeaRequest) => void
}) => {
  const [form, setForm] = useState<Form>(formOf(idea))
  const initial = useMemo(() => formOf(idea), [idea])
  useEffect(() => {
    if (open) setForm(formOf(idea))
  }, [open, idea])
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }))
  const dirty = JSON.stringify(form) !== JSON.stringify(initial)
  const urlBad = !!form.draft_url.trim() && !/^https?:\/\/\S+$/i.test(form.draft_url.trim())
  const canSave = !!form.title.trim() && !urlBad && (!idea || dirty)

  const submit = () => {
    const terms = form.terms
      .split('\n')
      .map((t) => t.trim())
      .filter(Boolean)
      .map((term) => ({ term }))
    onSave({
      title: form.title.trim(),
      type: form.type,
      // Origin is a fact about where the idea came from: set once, on create.
      ...(idea ? {} : { origin: form.origin }),
      priority: (form.priority || null) as ContentIdeaPriority | null,
      solutions: form.solutions,
      industries: form.industries,
      terms,
      rationale: blank(form.rationale),
      notes: blank(form.notes),
      draft_url: blank(form.draft_url),
    })
  }

  return (
    <Modal
      title={idea ? 'Edit idea' : 'New idea'}
      open={open}
      onClose={onClose}
      sx={{ width: 640 }}
      description="One idea per piece. Terms that match the keyword master link to it automatically."
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={loading} disabled={!canSave}>
            {idea ? 'Save' : 'Add idea'}
          </Button>
        </>
      }
    >
      <Stack spacing={4} mt={2}>
        <Input
          label="Working title"
          value={form.title}
          onChange={(v) => set('title', v)}
          placeholder="What is a digital product passport?"
        />
        <Stack direction="row" spacing={3}>
          <SelectField
            label="Type"
            options={CONTENT_IDEA_TYPES.map((t) => ({
              value: t,
              label: typeLabel(t),
            }))}
            value={form.type}
            onChange={(v) => set('type', v as ContentIdeaType)}
          />
          <SelectField
            label="Priority"
            options={[{ value: '', label: 'None' }, ...CONTENT_IDEA_PRIORITIES.map((p) => ({ value: p, label: p }))]}
            value={form.priority}
            onChange={(v) => set('priority', v as string)}
          />
          {idea ? null : (
            <SelectField
              label="Origin"
              options={ORIGIN_OPTIONS}
              value={form.origin}
              onChange={(v) => set('origin', v as ContentIdeaOrigin)}
            />
          )}
        </Stack>
        <SelectField
          label="Solutions"
          multiple
          placeholder="None"
          options={CONTENT_SOLUTIONS.map((s) => ({
            value: s,
            label: solutionLabel(s),
          }))}
          value={form.solutions}
          onChange={(v) => set('solutions', v as MetricSolution[])}
        />
        <SelectField
          label="Industries"
          multiple
          search
          placeholder="None"
          options={CONTENT_INDUSTRIES.map((i) => ({
            value: i,
            label: industryLabel(i),
          }))}
          value={form.industries}
          onChange={(v) => set('industries', v as ContentIndustry[])}
        />
        <Input
          label="Target terms (one per line, primary first)"
          value={form.terms}
          onChange={(v) => set('terms', v)}
          multiline
          minRows={3}
          placeholder={'digital product passport requirements\nespr deadline'}
        />
        <Input
          label="Why write it"
          value={form.rationale}
          onChange={(v) => set('rationale', v)}
          multiline
          minRows={2}
        />
        <Input label="Notes" value={form.notes} onChange={(v) => set('notes', v)} multiline minRows={2} />
        <Input
          label="Draft link"
          value={form.draft_url}
          onChange={(v) => set('draft_url', v)}
          placeholder="https://"
          error={urlBad}
          helperText={urlBad ? 'Start with http:// or https://' : undefined}
        />
      </Stack>
    </Modal>
  )
}
